/**
 * FeatureFlagsView — per-firm feature toggles (Phase 4/5).
 * Gated on firm.manage. Toggling a flag flips a capability domain on/off for
 * the whole org (e.g. Monitoring). The backend fails closed on unset flags.
 */
import React from 'react';
import { toast } from 'sonner';

import { Heading } from '../../ui/heading';
import { Switch } from '../../ui/switch';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../ui/card';
import { useAccess, Capability } from '../../../hooks/useAccess';
import { useFeatureFlags, useSetFeatureFlag } from '../../../hooks/useFeatureFlags';

/** Human labels + descriptions for known feature keys. */
const FEATURE_META: Record<string, { label: string; description: string }> = {
  monitoring: {
    label: 'Monitoring',
    description: 'Collections, records, and conduct classification.',
  },
  ombudsman: {
    label: 'Ombudsman (FOS)',
    description: 'FOS decision simulation and analysis.',
  },
};

const FeatureFlagsView: React.FC = () => {
  const { can } = useAccess();
  // Feature flags are a platform-internal control: both this route and the API
  // (rbac_admin feature-flag endpoints) authorize PLATFORM_MANAGE. Gating on
  // FirmManage locked out a home-context platform admin (platform and client
  // domains are separate, so they hold platform.manage but not firm.manage).
  const canManage = can(Capability.PlatformManage);
  const { data: flags, isLoading } = useFeatureFlags();
  const setFlag = useSetFeatureFlag();

  const handleToggle = async (featureKey: string, enabled: boolean) => {
    try {
      await setFlag.mutateAsync({ featureKey, enabled });
      toast.success(`${FEATURE_META[featureKey]?.label ?? featureKey} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update feature');
    }
  };

  if (!canManage) {
    return (
      <div className="p-6">
        <Heading level={2}>Features</Heading>
        <p className="mt-2 text-sm text-muted-foreground">
          You need platform administration access to manage features.
        </p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <Heading level={2}>Features</Heading>
        <p className="mt-1 text-sm text-muted-foreground">
          Turn product areas on or off for your organization.
        </p>
      </div>

      <div className="space-y-2">
        {isLoading && (
          <p className="text-sm text-muted-foreground">Loading features…</p>
        )}
        {flags?.map((flag) => {
          const meta = FEATURE_META[flag.featureKey];
          return (
            <Card key={flag.featureKey}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0">
                <div>
                  <CardTitle>{meta?.label ?? flag.featureKey}</CardTitle>
                  {meta?.description && (
                    <CardDescription>{meta.description}</CardDescription>
                  )}
                </div>
                <Switch
                  checked={flag.enabled}
                  disabled={setFlag.isPending}
                  onCheckedChange={(enabled) =>
                    handleToggle(flag.featureKey, enabled)
                  }
                />
              </CardHeader>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default FeatureFlagsView;
